import React, { useState, useEffect, useRef } from "react";
import { Select } from "antd";

// Helper function to set numeric keyboard attributes
const setNumericKeyboard = (element) => {
  if (element && typeof element.setAttribute === "function") {
    element.setAttribute("inputmode", "numeric");
    element.setAttribute("pattern", "[0-9]*");
  }
};

// Helper function to find and set numeric keyboard on input
const findAndSetNumericKeyboard = (containerRef, fallbackSelector = null) => {
  try {
    // Try to find input in container
    const container = containerRef?.current;
    if (container && typeof container.querySelector === "function") {
      const inputElement =
        container.querySelector(".ant-select-selection-search-input") ||
        container.querySelector("input");
      if (inputElement) {
        setNumericKeyboard(inputElement);
        return;
      }
    }

    // Fallback to document search
    if (fallbackSelector) {
      const inputElement = document.querySelector(fallbackSelector);
      if (inputElement) {
        setNumericKeyboard(inputElement);
      }
    }
  } catch (error) {
    console.warn("Error setting numeric keyboard:", error);
  }
};

export default function CustomTimePicker({
  value,
  onChange,
  availableHours = [],
  availableMinutes, // New prop: function to generate available minutes
  timeSlot, // New prop: {start, end}
  placeholder = { hour: "Hour", minute: "Minute" },
  style = {},
  size = "default",
  disabled = false,
  className = "",
}) {
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const hourSelectRef = useRef(null);
  const minuteSelectRef = useRef(null);

  // Generate minutes based on selected hour and time slot
  const generateMinuteOptions = () => {
    if (availableMinutes && typeof availableMinutes === "function") {
      return availableMinutes(selectedHour);
    }

    // Fallback: generate all minutes 0-59
    const minutes = [];
    for (let i = 0; i <= 59; i++) {
      minutes.push(i);
    }
    return minutes;
  };

  const minuteOptions = generateMinuteOptions();

  // Update parent when hour or minute changes
  useEffect(() => {
    if (selectedHour !== null && selectedMinute !== null) {
      const timeString = `${selectedHour
        .toString()
        .padStart(2, "0")}:${selectedMinute.toString().padStart(2, "0")}`;
      onChange?.(timeString);
    } else {
      onChange?.(null);
    }
  }, [selectedHour, selectedMinute, onChange]);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const [hour, minute] = value.split(":");
      setSelectedHour(parseInt(hour));
      setSelectedMinute(parseInt(minute));
    } else {
      setSelectedHour(null);
      setSelectedMinute(null);
    }
  }, [value]);

  // Generate available hours
  const generateAvailableHours = () => {
    if (availableHours) {
      return availableHours.map((hour) => ({
        value: hour,
        label: hour.toString().padStart(2, "0"),
      }));
    }

    // Default: all hours 0-23
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push({
        value: i,
        label: i.toString().padStart(2, "0"),
      });
    }
    return hours;
  };

  // Generate available minutes (0-59)
  const generateAvailableMinutes = () => {
    const minutes = [];
    for (let i = 0; i < 60; i++) {
      minutes.push({
        value: i,
        label: i.toString().padStart(2, "0"),
      });
    }
    return minutes;
  };

  // Handle hour change
  const handleHourChange = (hour) => {
    setSelectedHour(hour);

    // Reset minute when hour changes if current minute is not available for new hour
    if (selectedMinute !== null) {
      const newAvailableMinutes = availableMinutes
        ? availableMinutes(hour)
        : generateMinuteOptions();
      if (!newAvailableMinutes.includes(selectedMinute)) {
        setSelectedMinute(null);
      }
    }

    // Auto focus to minute select after selecting hour
    setTimeout(() => {
      if (minuteSelectRef.current) {
        minuteSelectRef.current.focus();
      }
    }, 100);
  };

  // Handle minute change
  const handleMinuteChange = (minute) => {
    setSelectedMinute(minute);
  };

  const getSelectedTime = () => {
    if (selectedHour !== null && selectedMinute !== null) {
      return `${selectedHour.toString().padStart(2, "0")}:${selectedMinute
        .toString()
        .padStart(2, "0")}`;
    }
    return null;
  };

  const availableHourOptions = generateAvailableHours();
  // Use the new generateMinuteOptions function instead
  // const availableMinuteOptions = generateAvailableMinutes();
  const selectedTime = getSelectedTime();

  return (
    <div style={{ ...style }} className={`time-picker-container ${className}`}>
      <div
        style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          border: "2px solid #e9ecef",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          position: "relative",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#495057",
          }}
        >
          🕐 เลือกเวลา
        </div>

        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "15px",
                color: "#14AE5C",
                textAlign: "center",
              }}
            >
              ชั่วโมง
            </label>
            <Select
              ref={hourSelectRef}
              placeholder={placeholder.hour}
              style={{
                width: "100%",
                height: size === "large" ? "50px" : "40px",
              }}
              value={selectedHour}
              onChange={handleHourChange}
              disabled={disabled}
              size={size}
              showSearch
              optionFilterProp="label"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{
                position: "fixed",
                zIndex: 9999,
                maxHeight: "200px",
                overflow: "auto",
              }}
              dropdownMatchSelectWidth={true}
              onFocus={() => {
                // Set numeric keyboard for mobile - Hour select
                setTimeout(() => {
                  findAndSetNumericKeyboard(
                    hourSelectRef,
                    ".ant-select-focused .ant-select-selection-search-input"
                  );
                }, 100);
              }}
              onDropdownVisibleChange={(open) => {
                if (open) {
                  setTimeout(() => {
                    findAndSetNumericKeyboard(
                      hourSelectRef,
                      ".ant-select-focused .ant-select-selection-search-input"
                    );
                  }, 200);
                }
              }}
              filterOption={(input, option) => {
                // Allow only numbers
                if (!/^\d*$/.test(input)) return false;

                // If empty input, show all options
                if (!input) return true;

                // Filter by available hours only
                const inputNum = parseInt(input);
                if (isNaN(inputNum)) return true;

                // Check if the number exists in available hours
                const availableValues = availableHourOptions.map(
                  (h) => h.value
                );
                return availableValues.includes(inputNum);
              }}
              onSearch={(value) => {
                // Restrict input to numbers only
                if (!/^\d*$/.test(value)) return;

                const num = parseInt(value);
                if (!isNaN(num)) {
                  const availableValues = availableHourOptions.map(
                    (h) => h.value
                  );
                  if (availableValues.includes(num)) {
                    handleHourChange(num);
                  }
                }
              }}
              onInputKeyDown={(e) => {
                // Handle Enter key to move to minute select
                if (e.key === "Enter" && selectedHour !== null) {
                  e.preventDefault();
                  // Focus on minute select
                  setTimeout(() => {
                    if (minuteSelectRef.current) {
                      minuteSelectRef.current.focus();
                    }
                  }, 100);
                }

                // Block non-numeric characters
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
            >
              {availableHourOptions.map((hour) => (
                <Select.Option
                  key={hour.value}
                  value={hour.value}
                  label={hour.label}
                >
                  {hour.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#14AE5C",
              paddingBottom: "10px",
            }}
          >
            :
          </div>

          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "15px",
                color: "#14AE5C",
                textAlign: "center",
              }}
            >
              นาที
            </label>
            <Select
              ref={minuteSelectRef}
              placeholder={placeholder.minute}
              style={{
                width: "100%",
                height: size === "large" ? "50px" : "40px",
              }}
              value={selectedMinute}
              onChange={handleMinuteChange}
              disabled={disabled || selectedHour === null}
              size={size}
              showSearch
              optionFilterProp="label"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{
                position: "fixed",
                zIndex: 9999,
                maxHeight: "200px",
                overflow: "auto",
              }}
              dropdownMatchSelectWidth={true}
              onFocus={() => {
                // Set numeric keyboard for mobile - Minute select
                setTimeout(() => {
                  findAndSetNumericKeyboard(
                    minuteSelectRef,
                    ".ant-select-focused .ant-select-selection-search-input"
                  );
                }, 100);
              }}
              onDropdownVisibleChange={(open) => {
                if (open) {
                  setTimeout(() => {
                    findAndSetNumericKeyboard(
                      minuteSelectRef,
                      ".ant-select-focused .ant-select-selection-search-input"
                    );
                  }, 200);
                }
              }}
              filterOption={(input, option) => {
                // Allow only numbers
                if (!/^\d*$/.test(input)) return false;

                // If empty input, show all options
                if (!input) return true;

                // Filter by available minutes
                const inputNum = parseInt(input);
                if (isNaN(inputNum)) return true;

                // Check if the number is within available minutes range
                return minuteOptions.includes(inputNum);
              }}
              onSearch={(value) => {
                // Restrict input to numbers only
                if (!/^\d*$/.test(value)) return;

                const num = parseInt(value);
                if (!isNaN(num) && minuteOptions.includes(num)) {
                  handleMinuteChange(num);
                }
              }}
              onInputKeyDown={(e) => {
                // Block non-numeric characters
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
            >
              {minuteOptions.map((minute) => (
                <Select.Option
                  key={minute}
                  value={minute}
                  label={minute.toString().padStart(2, "0")}
                >
                  {minute.toString().padStart(2, "0")}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {selectedTime && (
          <div
            style={{
              marginTop: 20,
              padding: "14px",
              background: "linear-gradient(135deg, #e8f5e8 0%, #f3f9f3 100%)",
              border: "2px solid #07C756",
              borderRadius: "16px",
              color: "#07C756",
              fontWeight: "700",
              textAlign: "center",
              fontSize: "16px",
              boxShadow: "0 4px 12px rgba(7, 199, 86, 0.15)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                width: 30,
                height: 20,
                borderRadius: "50%",
                background: "rgba(7, 199, 86, 0.1)",
              }}
            />
            <div style={{ position: "relative", zIndex: 1 }}>
              เวลาที่เลือก:{" "}
              <span style={{ fontSize: "16px" }}>{selectedTime}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
